import React, { useEffect, useState } from "react";
import gsap from "gsap";
import styled from "styled-components";


import ContentWidth from "components/ContentWidth";
import ScrollPinBG from "images/PinScroll/pin-scroll-bg.png";
import ScrollPinBGTb from "images/PinScroll/tablet-pin-scroll.svg";
import ScrollPinBGMob from "images/PinScroll/mobile-pin-scroll-bg.svg";
import Check from "images/PinScroll/checklist-orange-circle.svg";
import media from "styles/media";
import colors from "styles/colors";
import getMedia from "utils/getMedia";
import { vwToPx } from "utils/functions";
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const PinScroll = ({ content }) => {
    const [nextIndex, setNextIndex] = useState(0);
    const slideY = getMedia(-30, vwToPx(-2.083), vwToPx(-2.93), vwToPx(-7.93))
    const bg = getMedia(ScrollPinBG, ScrollPinBG, ScrollPinBGTb, ScrollPinBGMob)
    const opacity = getMedia(.5, .5, .5, 0)

      // COUNTER DATA
    const count = content?.topics.map((topic,index) => {
        return (
            <Number key={topic.headline} id='counter'>{`0${index + 1}`}</Number>
            )
      })

    // HEADLINE DATA
    const headlines = content?.topics.map((topic, index) => {
       return(
        <ActiveHeader id={`activeHeader-${index}`} key={topic.headline}>
            {topic.headline}
        </ActiveHeader>
       ) 
    })

     // CONTENT DATA
    const bodyData = content?.items.map((item, index) => {
        
        return (
            <ContentDiv key={item.bodyHeadline}>
            <Header id={`maskedText-${index}`} className='maskedText'>
                {item.bodyHeadline}
                <SecondaryWipeDiv className='wipe2Div'>
                    <WipeDiv className='wipeDiv' />
                </SecondaryWipeDiv>
            </Header>
            <Body id={`maskedText-${index}`} className='maskedText'>
                {item.bodyContent}
                <SecondaryWipeDiv className='wipe2Div'>
                    <WipeDiv className='wipeDiv' />
                </SecondaryWipeDiv>
            </Body>
            <List>
                {item.points.map(point =>
                    <Point key={point.text} id={`maskedText-${index}`} className='maskedText'>
                        <IconCheck src={Check} alt='iconCheck' />
                        <PointText>{point.text}</PointText>
                        <SecondaryWipeDiv className='wipe2Div'>
                            <WipeDiv className='wipeDiv' />
                        </SecondaryWipeDiv>
                    </Point>
                )}
            </List>
            {item.ctaLink ?
                <Link id={`maskedText-${index}`} className='maskedText' href={item.ctaLink}>
                    {item.ctaText} →
                    <SecondaryWipeDiv className='wipe2Div'>
                        <WipeDiv className='wipeDiv'/>
                    </SecondaryWipeDiv>
                </Link>  
                
         : null}
        </ContentDiv>
        )
    })

    useEffect(() => {
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: '#pinscrollWrapper',
                pin: ".pinned",
                pinSpacing: false,
                start: 'top top',
                end: 'bottom bottom',
                scrub: true,
            }
        });
        gsap.set('#maskedText-0', { opacity: 1})
        gsap.set('#activeHeader-0', {scale: 1.15, opacity: 1})

        tl.to('#activeHeader-0', {scale:1, opacity: opacity})
            .to('.wipeDiv', {
                height: '100%', 
            }, '<.5')
            .to('.wipe2Div', {
                height: '0%',
            }, '>')
            .set('#maskedText-0', {opacity: 0}, '<')
            .set('#maskedText-1', {opacity: 1}, '<')
            .to('#activeHeader-1', {scale: 1.15, opacity:1}, '<5%')
            .to('#slide', {y: slideY}, '<5%')
            .set(".wipeDiv", {
                height: '0%',
            }, '<.5')
            .set('.wipe2Div', {
                height: '100%',
            }, '>')
        
        .to('#activeHeader-1', {scale:1, opacity: opacity})
            .to('.wipeDiv', {
                height: '100%', 
            }, '<.5')
            .to('.wipe2Div', {
                height: '0%',
            }, '>')
            .set('#maskedText-1', {opacity: 0}, '<')
            .set('#maskedText-2', {opacity: 1}, '<')
            .to('#activeHeader-2', {scale: 1.15, opacity:1}, '<5%')
            .to('#slide', {y: slideY + slideY}, '<5%')
            .set(".wipeDiv", {
                height: '0%',
            }, '<.5')
            .set('.wipe2Div', {
                height: '100%',
            }, '>')

        .to('#activeHeader-2', {scale:1, opacity: opacity})
            .to('.wipeDiv', {
                height: '100%', 
            }, '<.5')
            .to('.wipe2Div', {
                height: '0%',
            }, '>')
            .set('#maskedText-2', {opacity: 0}, '<')
            .set('#maskedText-3', {opacity: 1}, '<')
            .to('#activeHeader-3', {scale: 1.15, opacity:1}, '<5%')
            .to('#slide', {y: slideY + slideY + slideY}, '<5%')
            .set(".wipeDiv", {
                height: '0%',
            }, '<.5')
            .set('.wipe2Div', {
                height: '100%',
            }, '>')

            return () => {
                tl.kill()
            }

    }, [nextIndex, headlines, slideY.current]);

    return (
        <ContentWidth>
            <Wrapper id='pinscrollWrapper'>
                <CenterDiv className='pinned'>
                    <Card bg={bg}>
                            <CurrentSectionContainer>
                                {headlines}
                            </CurrentSectionContainer>
                            <OverflowDiv className='pinned'>
                                <SlideDiv id='slide'>
                                    {count}
                                </SlideDiv>
                                <PointDiv>/0{headlines?.length}</PointDiv>
                            </OverflowDiv>
                    </Card>
                    <ContentContainer>
                        {bodyData}
                    </ContentContainer>
                </CenterDiv>
            </Wrapper>
        </ContentWidth>
    )
}

const Link = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  color: ${colors.primaryOrange};
  font-family: 'Archivo';
  opacity: 0;
  font-weight: 700;
  font-size:1.389vw;
  line-height: 1.667vw;
  gap: 1.042vw;
  margin-top: 40px;
  

  ${media.fullWidth}{
    font-size:20px;
    line-height: 24px;
    gap: 15px;
  }
  ${media.tablet}{
    font-size:1.953vw;
    line-height: 2.344vw;
    gap: 1.465vw;
  }
  ${media.mobile}{
    font-size: 5.333vw;
    line-height: 8vw;
    gap: 1.465vw;
  }
`
const SecondaryWipeDiv = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  will-change: transform;
  width: 100%;
  z-index: 2;
`
const WipeDiv = styled.div`
  position: absolute;
  bottom: 0;
  height: 0%;
  will-change: transform;
  width: 100%;
  z-index: 2;
  background-color: white;
`
const ActiveHeader = styled.h3`
  color: ${colors.white};
  font-family: 'Archivo';
  font-style: normal;
  font-weight: 400;
  font-size: 1.111vw;
  line-height: 1.667vw;
  opacity: .5;

  ${media.fullWidth} {
    font-size: 16px;
    line-height: 24px;
  }
  
  ${media.tablet} {
    font-size: 1.953vw;
    line-height: 2.93vw;  
  }
  
  ${media.mobile} {
    position: absolute;
    font-size: 6.4vw;
    line-height: 8vw;
    padding-left: 5.333vw;
    bottom: 9.667vw;
    font-weight: 700;
    opacity:0;
  }
`
const CurrentSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  justify-self: center;
  height: auto;
  width: auto;
`
const Number = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 2.083vw;
  margin-left: 0.278vw;
  color: white;

  ${media.fullWidth} {
    height: 30px;
    margin-left: 4px;
  }
  
  ${media.tablet} {
    height: 2.93vw;
  }
  
  ${media.mobile} {
    height: 8vw;
  }
`
const PointDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
  height: 100%;
  color: white;
  font-family: 'Archivo';
`
const SlideDiv = styled.div`
  color: white;
  width: 50%;
  height: auto;
  font-family: 'Archivo';
`
const OverflowDiv = styled.div`
  position: absolute;
  display: flex;
  align-self: flex-end;
  justify-self: flex-end;
  overflow: hidden;
  height: 2.083vw;
  width: 3.542vw;
  
  ${media.fullWidth} {
    height: 30px;
    width: 51px;
  }
  
  ${media.tablet} {
    height: 2.93vw;
    width: 4.98vw;
  }
  
  ${media.mobile} {
    left: 80%;
    bottom: 20%;
    height: 8vw;
    width: 13.6vw;
  }
`
const IconCheck = styled.img`
  width: 2.547vw;
  height: 2.547vw;

${media.fullWidth} {
    width: 36.67px;
    height: 36.67px;
}
${media.tablet} {
    width: 3.516vw;
    height: 3.516vw;
}
${media.mobile} {
    width: 9.779vw;
    height:9.779vw;
}
`

const PointText = styled.div`
 color: ${colors.grey700};
  font-family: 'Archivo';
  font-weight: 700;
  font-size:1.389vw;
  line-height: 1.667vw;

  ${media.fullWidth} {
    font-size:20px;
    line-height: 24px;
  }
  
  ${media.tablet} {
    font-size:1.953vw;
    line-height: 2.344vw;
  }
  
  ${media.mobile} {
    font-size: 5.333vw;
    line-height: 6.4vw;
    letter-spacing: 0em;
  }
`

const Point = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.042vw;
  opacity: 0;
  overflow: hidden;

  ${media.fullWidth} {
    gap: 15px;
  }
  ${media.tablet} {
    gap: 1.465vw;
  }
  ${media.mobile} {
    gap: 3.505vw;
  }
`
const List = styled.p`
  display: flex;
  flex-direction: column;
  gap: 2.292vw;

  ${media.fullWidth} {
    gap: 23px;
  }
  
  ${media.tablet} {
    gap: 3.223vw;
  }
  
  ${media.mobile} {
    gap: 3.533vw;
  }
`
const Body = styled.p`
  position: relative;
  margin-bottom: 1.944vw;
  font-family: 'Archivo';
  color: ${colors.grey800};
  font-weight: 400;
  font-size: 1.25vw;
  line-height: 1.875vw;
  opacity: 0;
  overflow: hidden;

  ${media.fullWidth} {
    margin-bottom: 28px;
    font-size: 18px;
    line-height: 27px;
  }
  
  ${media.tablet} {
    margin-bottom: 2.734vw;
    font-size: 1.758vw;
    line-height: 2.637vw;
  }
  
  ${media.mobile} {
    font-size: 4.8vw;
    line-height: 7.2vw;
    margin-bottom: 3.734vw;
  }
`
const Header = styled.h3`
  font-weight: 700;
  font-size: 2.361vw;
  line-height: 2.778vw;
  margin-bottom: 1.111vw;
  color: ${colors.grey800};
  position: relative;
  opacity: 0;
  overflow: hidden;

  ${media.fullWidth} {
    margin-bottom: 16px;
    font-size: 34px;
    line-height: 40px;
  }
  
  ${media.tablet} {
    margin-bottom: 1.563vw;
    font-size: 3.32vw;
    line-height: 3.906vw;
  }
  
  ${media.mobile} {
    display: none;
  }
`
const ContentDiv = styled.div`
  position: absolute;
`
const ContentContainer = styled.div`
  position: relative;
  height: auto;
  width: 47.222vw;

  ${media.fullWidth} {
    width: 680px;
  }
  
  ${media.tablet} {
    width: 57.031vw;
  }
  
  ${media.mobile} {
    width: 100vw;
    padding: 0vw 2.133vw;
  }
`
const Card = styled.div`
  display: flex;
  background-image: ${props => props.bg ? `url(${props.bg})` : ''};
  background-repeat: no-repeat;
  background-size: contain;
  padding: 0 0 1.389vw 1.944vw;
  border-radius: 2.083vw;
  width: 27.639vw;
  height: 44.028vw;

  ${media.fullWidth} {
    padding: 0 0 20px 28px;
    border-radius: 30px;
    width: 398px;
    height: 634px;
  }
  
  ${media.tablet} {
    width: 28.516vw;
    height: 61.914vw;
    border-radius: 2.93vw;
    padding: 0 0 1.953vw 2.734vw;
  }
  
  ${media.mobile} {
    width: 100vw;
    display: flex;
    flex-direction: row;
    border-radius: 0vw;
  }
`
const CenterDiv = styled.div`
  position: relative;
  display: flex;
  width: auto;
  height: auto;
  gap: 4.583vw;
  margin-top: 2.083vw;

  ${media.fullWidth} {
    gap: 66px;
  }
  
  ${media.tablet} {
    gap: 8.594vw;
  }
  
  ${media.mobile} {
    display: flex;
    flex-direction: column;
    gap: 1.594vw;
  }
`
const Wrapper = styled.div`
  height: 277.778vw;
  padding-top: 5.556vw;

  .pin-spacer {
    width: unset !important;
  }
 
  ${media.fullWidth} {
    height: 4000px;
    padding-top: 80px;
  }
  
  ${media.tablet} {
    height: 390.625vw;
    padding-top: 5.859vw;
  }
  
  ${media.mobile} {
    height: 800vw;
    width: 100vw;
  }
`

export default PinScroll